import { Component, Inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Filter } from 'src/app/interfaces/groups-filters/groups-filters.interface';
import { GroupInterface, GroupMemberInterface } from 'src/app/interfaces/groups/group.interface';
import { GroupsService } from 'src/app/services/groups/groups.service';
import {
  LoadItemsEvent,
  NamedId,
} from 'src/app/services/marketing-campaigns/marketing-campaigns-group/marketing-campaign-groups-ids-form';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { getTypeName } from '../../tabs/marketing-campaigns-groups/marketing-campaigns-groups.component';
import { MarketingCampaignCreateResult } from '../marketing-campaign-create/marketing-campaign-create.component';

// ------------------------
// Validators and Utilities
// ------------------------

export function notEmpty(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    control.value.length > 0 ? null : { notEmpty: true };
}

// ------------------------
// Component Declaration
// ------------------------

@Component({
  selector: 'app-groups-details',
  templateUrl: './groups-details.component.html',
  styleUrls: ['./groups-details.component.scss'],
})
export class GroupsDetailsComponent implements OnInit {
  @Input() public groupInterface: GroupMemberInterface = GroupMemberInterface.Team;

  protected readonly GroupMemberInterface = GroupMemberInterface;
  protected readonly labels = this.initLabels();

  protected groupInterfaceForm: FormControl = new FormControl({
    value: 'team',
    disabled: !!this.data?.group,
  });
  protected formSnapshots = new Map();

  protected selectedIds = new Set<string>();
  protected selectedItemsCount = 0;
  protected initialValues: boolean[] = [];

  protected items: NamedId[] = [];
  protected label = '';
  protected groupInterfaces = Object.values(GroupMemberInterface);
  protected groupId?: string;
  protected getTypeName = getTypeName;

  private pagination = {
    search: '',
    page: 1,
    limit: 50,
  };
  private filter: { filters: Filter[]; cacheId?: string } = { filters: [] };
  protected itemCount = 0;

  constructor(
    private spinnerService: SpinnerService,
    public dialogRef: MatDialogRef<MarketingCampaignCreateResult>,
    private groupsService: GroupsService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data?: { group: GroupInterface },
  ) {}

  // ------------------------
  // Lifecycle
  // ------------------------

  async ngOnInit(): Promise<void> {
    if (this.data?.group) {
      this.groupInterface = this.data.group.memberType;
      this.groupInterfaceForm.setValue(this.groupInterface);
      this.groupId = this.data.group.id;
    }
    this.dialogRef.backdropClick().subscribe(() => this.close());
    if (!this.isEmailGroup()) await this.loadItems();
  }

  // ------------------------
  // UI Events
  // ------------------------

  close(): void {
    this.dialogRef.close({ save: false });
  }

  async groupInterfaceChanged(_: MatSelectChange): Promise<void> {
    this.groupInterface = this.groupInterfaceForm.value;
    this.label = this.labels[this.groupInterface];
    if (!this.isEmailGroup) await this.loadItems();
  }

  async loadEvent(event?: LoadItemsEvent): Promise<void> {
    if (event?.pagination) {
      const { page, limit, search } = event.pagination;
      this.pagination = {
        ...this.pagination,
        ...(page !== undefined && { page }),
        ...(limit !== undefined && { limit }),
        ...(search !== undefined && { search }),
      };
    }
    if (event?.filters) {
      this.filter.filters = event.filters;
    }

    await this.loadItems();
  }

  saved(event: { data: any }) {
    this.dialogRef.close({ save: true, data: event.data });
  }

  // ------------------------
  // Main Actions
  // ------------------------

  isEmailGroup(): boolean {
    return this.groupInterface === GroupMemberInterface.EmailAddress;
  }

  // ------------------------
  // Data and Selection
  // ------------------------

  private async loadItems(): Promise<void> {
    try {
      this.spinnerService.show(`Loading ${this.label}...`);

      if (this.groupId) {
        this.selectedIds.clear();
        const groupDetails = (await this.groupsService.getById(this.groupId)) as GroupInterface;
        groupDetails.members.map((m) => this.selectedIds.add(m));
        this.selectedItemsCount = this.selectedIds.size;
      }

      const getMembersParams = {
        pagination: {
          ...this.pagination,
          selectedMembers: [...this.selectedIds],
        },
        filter: this.filter,
      };

      const res = await this.groupsService.getItemsByType(this.groupInterface, getMembersParams);
      if (res.success === false) throw new Error(res.error);
      const data = res.data;

      this.itemCount = data.count;
      this.items = data.members;
      if (this.filter && data.cacheId) {
        this.filter.cacheId = data.cacheId;
      }

      if (this.groupId)
        this.initialValues = this.items.map((item) => this.selectedIds.has(item.id));
    } catch (err) {
      console.error(err);
      this.snackBar.open(`Error while loading ${this.label}. Try again in a few minutes.`, '', {
        duration: 2000,
      });
    } finally {
      this.spinnerService.hide();
    }
  }

  // ------------------------
  // Static Maps
  // ------------------------

  private initLabels() {
    return {
      [GroupMemberInterface.Team]: 'teams',
      [GroupMemberInterface.Company]: 'companies',
      [GroupMemberInterface.Brand]: 'brands',
      [GroupMemberInterface.EmailAddress]: 'emails',
      [GroupMemberInterface.ExistingUser]: 'users',
    };
  }
}
